import axios from "axios";
import {IOwayoDataModel} from "../model";
import {IEntwurfTransferProvider} from "../controller";
import {EventListener, EventVerteiler} from "@k/global/eventVerteiler";
import {ChromoLogo, Logo, SelectableObject, StockLogo, TextObject} from "../selectableObjects";
import {ColorUtils} from "../colorUtils";
import {ColorController} from "./colorController";
import {OUTLINELEVELS, Textoutline} from "../textoutline";
import {FontHandler} from "../fontHandler";
import {Vector2} from "../delme_globals";
import {CONSTANTS} from "../meta_data";
import {CONTROLLER_EVENTNAMES, HINTBOXEVENTNAMES, SELOBEVENTNAMES} from "@k/global/GlobalEventnames";
import {LogoPoolController} from "./logoPoolController";
import {createGUID, valuesAreEqualWithinPrecision} from "@k/global/globalUtils";

import {OwayoJSKonfiViewer} from "../owayoJSViewerWithSelObs";
import {EinzelExemplar, ExemplarListenText} from "@k/global/einzelexemplare/einzelExemplarObject";
import globalsObject from "../globalsObject";
import DEBUG from "../DEBUG";
import {Color} from "../colorObject";
import {deepCopy} from "@k/global/deepCopy";
import {SelObTypes} from "../SelObTypes";
import {arrayElementByKeyValue, arrayIndexByKeyValue} from "@k/global/arrayHelper";
import {LogoPoolEntry} from "../databaseInterfaceClasses";
import {AfterTimeoutExecutor} from "../../../konfi_mobile/src/js/afterTimeoutExecutor";
import {OwayoKonfiPart} from "@k/js/owayoPartWithSelObs";

export class PPIInfo {
    public ppi:Vector2;

    constructor() {
        this.ppi = new Vector2(-1, -1);
    }

    public getMinPPI = () => Math.round(Math.min(this.ppi.x, this.ppi.y));
    public isBelowThreshhold = () => this.getMinPPI() < CONSTANTS.MIN_PIXEL_PER_INCH;

}

export interface SelObInteractor {
    getSelOb:(id:string)=>SelectableObject|null;
    changeSelOb:(id, parameters, snapping?)=>void;
    deleteSelOb:(id,deletePermanent?, origin?)=>void
}

export class SelObController extends EventListener implements SelObInteractor{
    public model: IOwayoDataModel;
    private entwurfTransferProvider: IEntwurfTransferProvider;
    private readonly tracker;
    private preisExecutor : AfterTimeoutExecutor;


    public constructor(model: IOwayoDataModel, entwurfTransferProvider:IEntwurfTransferProvider, tracker:{ trackPageview }|null=null) {
        super();
        this.model = model;
        this.entwurfTransferProvider = entwurfTransferProvider;
        this.tracker = tracker;
        this.initEventListeners();
        this.preisExecutor = new AfterTimeoutExecutor(500, ()=>EventVerteiler.broadcast("getPreisEvent"));
    }

    protected trackPageView(page) {
        if(this.tracker === null) {
            return;
        }
        this.tracker.trackPageview(page);
    }

    private initEventListeners() {
        this.addEventListener(SELOBEVENTNAMES.CHECK_Z_ORDERTABLE, ()=>this.checkZOrderTable())
            .addEventListener(SELOBEVENTNAMES.LOAD_KK_VORBELEGUNGSSELOBS, (callback)=>this.loadKK_VorbelegungsSelObs(callback))
            .addEventListener(CONTROLLER_EVENTNAMES.CHANGESELOBWITHHISTORY, (data)=>this.changeSelObAndTellHistoryController(data.id, data.changedProperties))
            .addEventListener(SELOBEVENTNAMES.DELETEREQUEST, (id)=>this.deleteSelOb(id, true, "byUser"))
            .addEventListener(SELOBEVENTNAMES.DELETEREQUEST_WithoutHistory, (id)=>this.deleteSelOb(id, true, "notByUser"))
            .addEventListener(SELOBEVENTNAMES.SELECTION_REQUEST, (id)=>EventVerteiler.broadcast(SELOBEVENTNAMES.SELECTED, this.model.entwurf.getSelectableObject(id)))
            .addEventListener("relsizeCalculatedInViewerEvent", (data)=>this.relSizeCalculated(data.id, data.relsize, data.sizeCM))
            .addEventListener("createTextObjectFromFrontEndEvent", (data)=>this.createTextObject(data.textvalue, data.isFixeValue, data.part, data.position))
            .addEventListener("createLogoObjectFromFrontEndEvent", (data)=>this.directlyInsertNewLogoObject(data.filename, data.part, data.position))
            .addEventListener("setTackleTwillVisibility", (data)=>this.deleteTackleTwillInfosBeiBedarf(data))
            .addEventListener(SELOBEVENTNAMES.FONTLOADINGERROR_SET_TO_DEFAULTFONT, (data:{font:string, id:string})=>this.setTextObjectToDefaultFont(data))
        ;
    }

    private deleteTackleTwillInfosBeiBedarf(tackleTwillMoeglich:boolean){
        if(!tackleTwillMoeglich){
            this.model.entwurf.getLogoObjects().map((function(logo){
                logo.hasTackleTwill = false;
                logo.berechnungsFeature = null;
                return logo;
            }));

            (this.model.entwurf.getTextObjects() as TextObject[]).map((function(text){
                text.hasTackleTwill = false;
                text.berechnungsFeature = null;
                return text;
            }));
        }
    }

    private directlyInsertNewLogoObject(filename, part, position) {
        const logoPoolEntry = this.model.logoPool.getLogoByFilename(filename)
        if(logoPoolEntry === null) {
            console.warn("logo "+filename+" nicht gefunden");
            return;
        }
        const logo = LogoPoolController.createLogoObjectStatic(logoPoolEntry);
        this.model.entwurf.addLogo(logo);
        EventVerteiler.broadcast(SELOBEVENTNAMES.CREATED, logo);
        if(part !== null) {
            this.insertSelObById(logo.id, part, position.x, position.y, "insertFromLibrary");
            globalsObject.resourceManager.createTask("selectObject").discardAfterExecution().when(logo.id+"_initialized", "done")
                .thenDo(()=>EventVerteiler.broadcast(SELOBEVENTNAMES.SELECTED, this.model.entwurf.getSelectableObject(logo.id)))
        }
    }

    private getTextsAndLogos():SelectableObject[] {
        return (this.model.entwurf.getLogoObjects() as SelectableObject[]).concat((this.model.entwurf.getTextObjects() as TextObject[]) as SelectableObject[]);
    }



    public parthasSelObs(partName:string):boolean {
        return this.getTextsAndLogos().some(selOb=>selOb.partName === partName);
    }

    public getSelOb = id => this.model.entwurf.getSelOb(id);
    public getZIndexOfSelOb = (id:string)=>this.model.entwurf.getZOrderTable().getZIndex(id);
    public getCloneIdsForOrigId = (origId:string)=>(this.getSelOb(origId) as SelectableObject).cloneIds;

    public changeSelObAndTellHistoryController(id, changedProperties):void {
        EventVerteiler.broadcast("objectInteractionStartingEvent", id);
        this.changeSelOb(id, changedProperties);
        EventVerteiler.broadcast("objectInteractionEndingEvent", id);
    }

    private static isColorParameter(parameters) {
        return typeof (parameters.colorValue) !== "undefined" ||
            typeof (parameters.outlineColorValue) !== "undefined" ||
            typeof(parameters.colors) !== "undefined";
    }

    public changeSelOb(id, parameters, snapping = true) {
        globalsObject.resourceManager.executeInvalidation("changing_"+id);
        const selectableObject = this.model.entwurf.getSelectableObject(id);
        if (selectableObject === null) {
            globalsObject.errorHandler.handleError("internal", "changeSelOb", "Could not find selectableObject with id " + id);
            return;
        }

        for (const property in parameters) {
            this.changeSelObProperty(selectableObject, property, parameters, snapping);
        }
        if (SelObController.isColorParameter(parameters)) {
            EventVerteiler.broadcast("broadcastUsedColorsEvent");
        }
        if(parameters.colors !== undefined) {
            EventVerteiler.broadcast("logoColorChanged", {selOb:selectableObject, colors:parameters.colors})
        }
        if (typeof (parameters.font) !== "undefined") {
            this.changeSelObOutlinejoin(selectableObject as TextObject, parameters.font);
            this.sendUsedFontsToViewComponents();
        }
        this.model.entwurf.setChangedSinceSave(true);
        const changedProperties = Object.keys(parameters);
        EventVerteiler.broadcast(
            SELOBEVENTNAMES.CHANGED, {
                selOb: selectableObject,
                changedProperties: changedProperties
            });

        this.sendChangeEventToClones(selectableObject, changedProperties);
    }

    private changeSelObOutlinejoin(selectableObject:TextObject, font) {
        const $this = this;
        const parameters = {outlineLineJoin:null};
        if (globalsObject.resourceManager.resourceHasState("fonts", "loaded")) {
            parameters.outlineLineJoin = FontHandler.getOutlineLineJoin(font);
            this.changeSelObGeneralProperty(selectableObject, "outlineLineJoin", parameters);
        } else {
            globalsObject.resourceManager.createTask("updateOutlineLinejoin" + selectableObject.id)
                .when("fonts", "loaded")
                .thenDo(
                    function () {
                        const param = {outlineLineJoin: FontHandler.getOutlineLineJoin(font)};
                        $this.changeSelOb(selectableObject.id, param);
                    }
                );
        }
    }

    public deleteSelOb(id:string, deletePermanent = true, origin?:string) {
        let index:number, i:number;
        const selOb = this.getSelOb(id) as SelectableObject;
        const parentName = selOb.partName;
        if (origin === "byUser") {
            EventVerteiler.broadcast("selObAboutToBeDeletedByUserEvent", this.getSelOb(id));
        }
        const __ret = this.getIndexAndArrayOfSelOb(id);
        index = __ret.index;
        const arr = __ret.arr;
        if (index === -1) {
            globalsObject.errorHandler.handleError("severe", "deleteSelOb", "Could not delete Object " + id);
            return;
        }

        const cloneIds = arr[index].cloneIds;
        this.deleteSelObFromExemplarListe(id, cloneIds);
        for (i = cloneIds.length - 1; i > -1; i--) {
            this.model.entwurf.getZOrderTable().deleteEntry(cloneIds[i]);
            EventVerteiler.broadcast(SELOBEVENTNAMES.CLONE_DELETED, {origId: id, cloneId: cloneIds[i]});
            this.model.entwurf.deleteSelObById(cloneIds[i]);
        }

        //neuBerechnung Index, da aus dem Array Elemente gelöscht wurden!
        index = arrayIndexByKeyValue(arr, "id", id);


        if (deletePermanent) {
            arr.splice(index, 1);
        } else {
            arr[index].cloneIds = [];
            arr[index].clonedByPortalLineNumber = [];
        }

        this.model.entwurf.getZOrderTable().deleteEntry(id);

        EventVerteiler.broadcast(SELOBEVENTNAMES.DELETED, {id: id, fromPart:parentName});
        EventVerteiler.broadcast("getPreisEvent");
    }

    public deleteSelObFromExemplarListe(origId:string, cloneIds:string[]) {
        const $this = this;
        if(this.model.entwurf.getExemplarListe().length === 0) {
            return;
        }
        this.model.entwurf.getExemplarListe().forEach((e:EinzelExemplar)=>$this.deleteSelObFromExemplar(e, origId, cloneIds));
    }

    public deleteSelObFromExemplar(exemplar:EinzelExemplar, selObid:string, cloneIds:string[]) {
        exemplar.texts = exemplar.texts.filter((e:ExemplarListenText)=>e.masterID !== selObid && cloneIds.indexOf(e.masterID) === -1);
    }




    private getIndexAndArrayOfSelOb(id:string) {
        let index:number, arr:any[];
        index = arrayIndexByKeyValue(this.model.entwurf.getTextObjects(), "id", id);
        if (index >= 0) {
            arr = this.model.entwurf.getTextObjects();
        } else {
            index = arrayIndexByKeyValue(this.model.entwurf.getLogoObjects(), "id", id);
            arr = this.model.entwurf.getLogoObjects();
        }
        return {index: index, arr: arr};
    }


    private setPropertyOnSelObAndClones(selOb:SelectableObject, property:string, value:any) {
        selOb[property] = value;
        for (let i = 0, lenI = selOb.cloneIds.length; i < lenI; i++) {
            (this.model.entwurf.getSelectableObject(selOb.cloneIds[i]) as SelectableObject)[property] = value;
        }
    }

    private sendChangeEventToClones(selectableObject:SelectableObject, changedProperties) {
        let i;
        const anzClones = selectableObject.cloneIds.length;
        for (i = 0; i < anzClones; i++) { //evtl. wurde im obigen Event ein Klon gelöscht!
            EventVerteiler.broadcast(
                "clonedSelObChangedEvent", {
                    selOb: this.model.entwurf.getSelectableObject(selectableObject.cloneIds[i]),
                    changedProperties: changedProperties
                }
            );
        }
    }

    private changeSelObProperty(selectableObject:SelectableObject, property, parameters, snapping) {
        if (typeof (selectableObject[property]) === "undefined") {
            globalsObject.errorHandler.handleError("internal", "changeSelOb", "error setting SelectableObject Value " + property);
            return;
        }

        const map = {
            "rotation":()=>this.changeSelObRotation(selectableObject, parameters.rotation, snapping),
            "relSize":()=>this.changeSelObRelSize(selectableObject, parameters.relSize),
            "translation":()=>this.changeSelObTranslation(selectableObject, parameters.translation),
            "colors":()=>this.changeColorForSelObAndClones(selectableObject as ChromoLogo, parameters.colors),
            "outlineColorValue":()=>this.changeSelObOutlineColorValue(selectableObject as TextObject, parameters.outlineColorValue),
            "outlineWidth":()=>this.changeSelObOutlineWidth(selectableObject as TextObject, parameters.outlineWidth),
            "floating":()=>this.changeSelObFloating(selectableObject, parameters.floating)
        };
        if(typeof(map[property])!== "undefined") {
            map[property]();
        } else {
            this.changeSelObGeneralProperty(selectableObject, property, parameters);
        }
        if(property === "isPinned") {
            if(typeof((selectableObject as StockLogo).zuLoeschenBeiProduktUndDesignWechsel) !== "undefined") {
                (selectableObject as StockLogo).zuLoeschenBeiProduktUndDesignWechsel = false;
                selectableObject.cloneIds.forEach(c=>(this.getSelOb(c) as StockLogo).zuLoeschenBeiProduktUndDesignWechsel = false)
            }
        }
    }

    private changeColorForSelObAndClones(selectableObject:ChromoLogo, color) {
        const $this = this;
        SelObController.changeSelObColor(selectableObject, color);
        selectableObject.cloneIds.forEach(id=>SelObController.changeSelObColor($this.model.entwurf.getSelectableObject(id) as ChromoLogo, color));
    }

    private static changeSelObColor(selOb:ChromoLogo, color) {
        let colorObject = arrayElementByKeyValue(selOb.colors, "colorCode", color.colorCode);
        if (colorObject === null) {
            colorObject = arrayElementByKeyValue(selOb.colors, "colorCode", "#"+color.colorCode);
        }
        colorObject.colorIntValue = ColorUtils.hexToDec(color.colorHexValue);
        colorObject.NameImWeb = color.NameImWeb || "";
        selOb.createCurrentSVG();
    }

    private changeSelObOutlineColorValue(selectableObject:TextObject, outlineColorValue) {

        //Sonderbehandlung, da die outlineColorValue auch den Stringwert "<none>" annehmen kann. Unschön!
        this.setPropertyOnSelObAndClones(selectableObject, "outlineColorValue", outlineColorValue);
        if (selectableObject.outlineColorValue === "<none>") {
            selectableObject.outlineWidth = 0;
            selectableObject.outlineWidthLevel = NaN;
            return;
        }
        if (selectableObject.outlineWidth === 0) {
            const textHeight = Textoutline.getEffectiveHeightCMForOutlineComputation(selectableObject);
            const newOutlineWidth = globalsObject.owayoJSViewer.getHeightInPixelsFromHeightInCMRespectingScaling(selectableObject.id, Textoutline.getOutlineHeightInCM(textHeight,
                OUTLINELEVELS.OPTIMAL));
            this.setPropertyOnSelObAndClones(selectableObject, "outlineWidth", newOutlineWidth);
            this.setPropertyOnSelObAndClones(selectableObject, "outlineWidthLevel", OUTLINELEVELS.OPTIMAL);
        }
    }

    private static seeIfVec2HasStringValuesAndCompute(vec2:Vector2, oldValue:Vector2) {
        if (typeof (vec2.x) === "string") {
            vec2.x = OwayoJSKonfiViewer.computeNewNumberValue(vec2.x, oldValue.x);
        }
        if (typeof (vec2.y) === "string") {
            vec2.y = OwayoJSKonfiViewer.computeNewNumberValue(vec2.y, oldValue.y);
        }
        return vec2;
    }

    private changeSelObFloating(selOb:SelectableObject, floating) {
        this.setPropertyOnSelObAndClones(selOb, "floating", floating);
        switch (floating) {
            case CONSTANTS.FLOAT_FREE:
                break;
            case CONSTANTS.FLOAT_BOTTOM:
                this.changeZOrder(selOb.id, 0);
                break;
            case CONSTANTS.FLOAT_TOP:
                this.changeZOrder(selOb.id, this.model.entwurf.getZOrderTable().values.length - 1);
                break;
            default:
                alert("unknown floating Value " + floating);
        }
        EventVerteiler.broadcast(SELOBEVENTNAMES.FLOATING_CHANGED, {id: selOb.id, floating: floating});
    }

    private changeRotationOnClones(selectableObject:SelectableObject, oldValue:number, newValue:number) {
        for (let i = 0; i < selectableObject.cloneIds.length; i++) {
            (this.model.entwurf.getSelectableObject(selectableObject.cloneIds[i]) as SelectableObject).rotation += newValue - oldValue;
        }
    }

    private changeSelObRotation(selectableObject:SelectableObject, newRotation, snapping) {
        let i;
        const anzAngleSnapAnchors = CONSTANTS.ANGLE_SNAP_ANCHORS.length;
        const oldValue = selectableObject.rotation;
        let newValue = OwayoJSKonfiViewer.computeNewNumberValue(newRotation, selectableObject.rotation);
        if (isNaN(newValue)) {
            globalsObject.errorHandler.handleError("internal", "changeSelOb", "error setting number");
            return;
        }
        if (snapping) {
            for (i = 0; i < anzAngleSnapAnchors; i++) {
                if (Math.abs(newValue - CONSTANTS.ANGLE_SNAP_ANCHORS[i]) < globalsObject.owayoJSViewer.zoomController.currentAngleSnapRange()) {
                    newValue = CONSTANTS.ANGLE_SNAP_ANCHORS[i];
                    break;
                }
            }
        }
        if (newValue < -180) {
            newValue += 360;
        }
        if (newValue > 180) {
            newValue -= 360;
        }
        selectableObject.rotation = newValue;
        this.changeRotationOnClones(selectableObject, oldValue, newValue);
    }

    private changeSelObRelSize(selectableObject:SelectableObject, relSize:Vector2) {
        if(selectableObject.type === SelObTypes.TEXT) {
            SelObController.guessOutlineLevelIfNecessary(selectableObject as TextObject);
        }
        const oldValue = new Vector2(selectableObject.relSize.x, selectableObject.relSize.y);

        relSize = SelObController.seeIfVec2HasStringValuesAndCompute(relSize, oldValue);
        selectableObject.relSize = relSize;
        this.adjustCloneVector2FromOldValue(selectableObject, "relSize", {
            x: relSize.x - oldValue.x,
            y: relSize.y - oldValue.y
        });
    }

    private changeSelObTranslation(selectableObject:SelectableObject, translation:Vector2) {
        const oldValue = new Vector2(selectableObject.translation.x, selectableObject.translation.y);
        translation = SelObController.seeIfVec2HasStringValuesAndCompute(translation, oldValue);
        selectableObject.translation = translation;
        this.adjustCloneVector2FromOldValueAndRotate(selectableObject, "translation", {
            x: translation.x - oldValue.x,
            y: translation.y - oldValue.y
        });
    }

    private adjustCloneVector2FromOldValueAndRotate(selectableObject:SelectableObject, property:string, deltaVector:{x:number, y:number}) {
        const anzClones = selectableObject.cloneIds.length;
        for (let i = 0; i < anzClones; i++) {
            const clone = this.model.entwurf.getSelectableObject(selectableObject.cloneIds[i]);
            const newVec = globalsObject.owayoJSViewer.rotateRelVectorOnPart(
                deltaVector,
                clone.partName,
                selectableObject.cloningAngleRad[selectableObject.cloneIds[i]],
                clone.cloningAngleRad[selectableObject.id]
            );
            clone[property].x += newVec.x;
            clone[property].y += newVec.y;
        }
    }

    private changeSelObOutlineWidth(selectableObject:TextObject, outlineWidth) {
        let newValue = OwayoJSKonfiViewer.computeNewNumberValue(outlineWidth, selectableObject.outlineWidth);

        if (isNaN(newValue) || newValue < 1) {
            newValue = 1;
        }
        this.setPropertyOnSelObAndClones(selectableObject, "outlineWidth", newValue);
    }

    private changeSelObGeneralProperty(selectableObject:SelectableObject, property, parameters) {
        let newValue;
        if (typeof (selectableObject[property]) === "number") {
            // Hab über die nächste Zeile nachdenken müssen:
            //selectableObjekt[property] ist eine Zahl, nicht unbedingt parameters[property]!
            newValue = OwayoJSKonfiViewer.computeNewNumberValue(parameters[property], selectableObject[property]);
            if (isNaN(newValue)) {
                if (DEBUG) {
                    alert("ungültiger Wert in changelSelOb! (" + property + ": " + parameters[property] + ")");
                }
                return;
            }
        } else { //Typ ist nicht "number" aber vorhanden. Einfach den Wert übernehmen
            newValue = parameters[property];
        }

        if (selectableObject[property] === newValue) {
            return;
        }

        this.setPropertyOnSelObAndClones(selectableObject, property, newValue);
    }


    public changeZOrder(id, newZIndex, origin?) {

        const currentZIndex = this.model.entwurf.getZOrderTable().getZIndex(id);
        this.model.entwurf.getZOrderTable().changeZOrder(currentZIndex, newZIndex);

        this.model.entwurf.setChangedSinceSave(true);
        /**
         * Die ZOrder hat sich verändert
         * @event zOrderChangedEvent
         * @type {object}
         * @property {string}   id                  die id des Objekts, für die dieses Event ausgelöst wurde
         * @property {string[]} changed             Ein Array mit den IDs der Objekte, von denen die Veränderung ausging
         * @property {string}   completeJSONList    die komplette Liste der zIndizes mit allen ids als JSON(damit keine Referenzen übergeben werden)
         * @property {number}   oldIndex            der vorherige Z-Wert
         * @property {number}   newIndex            der neue Z-Index
         * @property {boolean}     byUser              gibt an, ob das direkt vom User angestoßen wurde oder z.B. vom HistoryController
         */
        EventVerteiler.broadcast("zOrderChangedEvent", {
            id: id,
            changed: this.model.entwurf.getZOrderTable().values[newZIndex].slice(0),
            completeJSONList: JSON.stringify(this.model.entwurf.getZOrderTable().values),
            oldIndex: currentZIndex,
            newIndex: newZIndex,
            byHistoryController: (origin === "historyController")
        });

    }


    private adjustCloneVector2FromOldValue(selectableObject:SelectableObject, property, deltaVector) {
        let i, clone;
        const anzClones = selectableObject.cloneIds.length;
        for (i = 0; i < anzClones; i++) {
            clone = this.model.entwurf.getSelectableObject(selectableObject.cloneIds[i]);
            clone[property].x += deltaVector.x;
            clone[property].y += deltaVector.y;
        }
    }


    public changeIsPinned = (id, value?) => this.toggleSelObProperty(id, "isPinned", value);
    public changeIsFixed = (id, value?) => this.toggleSelObProperty(id, "isFixed", value);
    public changeHasTackleTwill (id, value?) {
        this.toggleSelObProperty(id, "hasTackleTwill", value);
        this.setBerechnungsFeatureForTackleTwill(id);
    }

    private toggleSelObProperty(id, property, value?) {
        const selOb = this.model.entwurf.getSelectableObject(id) as SelectableObject;
        if (typeof (selOb[property]) === "undefined") {
            return;
        }
        const newValue = value !== undefined ? value : !selOb[property];
        const param = {};
        param[property] = newValue;
        this.changeSelOb(id, param);
    }

    public setBerechnungsFeatureForTackleTwill(id){
        const selOb = this.model.entwurf.getSelectableObject(id) as SelectableObject;
        if(!selOb.hasTackleTwill){
            selOb.berechnungsFeature = null;
            selOb.hasTackleTwill = false;
        }
        else{
            selOb.berechnungsFeature = this.getBerechnungsFeatureTackleTwillForSelOb(selOb);
            if(selOb.berechnungsFeature===undefined || selOb.berechnungsFeature===null || selOb.berechnungsFeature===""){
                selOb.hasTackleTwill = false;
            }
        }

        this.preisExecutor.executeAfterTimeout();
    }

    public setDPI(id){
        const selOb = this.model.entwurf.getSelectableObject(id) as SelectableObject;
        if (selOb.type !== "text"){
            const dpiInfo = this.getPixelPerInch(id, globalsObject.owayoJSViewer.getPartSelObSizeInCM(selOb.id));
            selOb.dpi =  dpiInfo.getMinPPI();
        }
        else{
            selOb.dpi = null;
        }
    }

    private getBerechnungsFeatureTackleTwillForSelOb(selOb:SelectableObject) : string{
        if (selOb.type !== "text"){
            return this.getBerechnungsFeatureTackleTwillForLogo(globalsObject.owayoJSViewer.getPartSelObSizeInCM(selOb.id));
        }
        else{
            return this.getBerechnungsFeatureTackleTwillForText(globalsObject.owayoJSViewer.getPartSelObSizeInCM(selOb.id), (selOb as TextObject).value);
        }
    }

    private getBerechnungsFeatureTackleTwillForLogo(sizeInCM : Vector2) : string{
        let berechnungsFeature = "Großes Logo Tackle-Twill";
        try {
            if(sizeInCM.x*sizeInCM.y < 100){
                berechnungsFeature = "Kleines Logo Tackle-Twill";
            }
        } catch (error) {
            console.log('Error:', error.message);
        }

        return berechnungsFeature;
    }

    private getBerechnungsFeatureTackleTwillForText(sizeInCM : Vector2, inhalt : string) : string{
        let berechnungsFeature = "Große Nummer Tackle-Twill";

        try{
            if(isNaN(parseInt(inhalt))){
                berechnungsFeature = "Spielername Tackle-Twill";
            }
            else{
                berechnungsFeature = (sizeInCM.y<10) ? "Kleine Nummer Tackle-Twill" : "Große Nummer Tackle-Twill";
            }
        } catch (error) {
            console.log('Error:', error.message);
        }

        return berechnungsFeature;
    }

    public insertSelObById(id, partname, x, y, origin?) {

        const selOb = this.model.entwurf.getSelectableObject(id) as SelectableObject;
        this.insertSelOb(selOb, partname, x, y, origin);
        this.setDPI(id);
    }

    public insertSelOb(selOb:SelectableObject, partname:string, x, y, origin?) {
        selOb.translation.x = x;
        selOb.translation.y = y;
        selOb.partName = partname;

        this.model.entwurf.setChangedSinceSave(true);

        const zIndex = this.model.entwurf.getZOrderTable().insertEntry(selOb.id, selOb.cloneIds, selOb.floating);
        selOb.zOrder = zIndex;

        EventVerteiler.broadcast(SELOBEVENTNAMES.INSERTED, {selOb: selOb, zIndex: zIndex});
        if (["insertFromLibrary"].indexOf(origin)===-1) {
            EventVerteiler.broadcast(SELOBEVENTNAMES.SELECTED, selOb);
        }
        EventVerteiler.broadcast(HINTBOXEVENTNAMES.HIDEHINT);
        this.trackPageView(selOb.type === SelObTypes.TEXT ? "konfiaccess/3Dtextgeaendert.htm" : "konfiaccess/3Dlogogeaendert.htm");

        if (origin === "byUser") {
            EventVerteiler.broadcast("selObInsertedByUserEvent", selOb);
        }
    }

    public moveSelObToPart(id, partname, position, newRelSize) {
        this.deleteSelOb(id, false);
        const selOb = this.model.entwurf.getSelectableObject(id) as SelectableObject;
        selOb.relSize = new Vector2(newRelSize.x, newRelSize.y);
        this.insertSelObById(id, partname, position.x, position.y);
        EventVerteiler.broadcast("designtexturesLoaded", []);
    }

    /**
     * Nach Initalisierung eines PartSelectableObjects steht jetzt die Translation des Objekts fest (unterschied Textmitte vs Bildmitte bei Textobjekten!)
     */
    public relTranslationCalculated(id:string, reltranslation:Vector2) {
        const selOb = this.model.entwurf.getSelectableObject(id);
        if (selOb !== null) {
            selOb.translation = new Vector2(reltranslation.x, reltranslation.y);
        } else {
            globalsObject.errorHandler.handleError("internal", "relTranslationCalculated", "Vorsicht, konnte reltranslation von " + id + " nicht setzen!");
        }
    }

    public cloneObject(parameters) {
        const id = parameters.id;
        const newPart = parameters.newPart;
        const newTranslation = parameters.newTranslation;
        const newRotation = parameters.newRotation;
        const newRelSize = parameters.newRelSize;
        const origCloningAngleRad = parameters.origCloningAngleRad;
        const cloneCloningAngleRad = parameters.cloneCloningAngleRad;

        const selOb = this.model.entwurf.getSelOb(id);
        if (selOb === null) {
            globalsObject.errorHandler.handleError("severe", "cloneObject", "could not clone object with id " + id);
            return;
        }
        const zIndex = this.model.entwurf.getZOrderTable().getZIndex(selOb.id);
        //Hier keine Anpassung nötig, da keine zirkuläre Referenz vorhanden!
        const clone = deepCopy(true,SelectableObject.createSelObFromType(selOb.type), selOb);

        clone.id = createGUID();
        for (let i = 0, len = selOb.cloneIds.length; i < len; i++) {
            (this.model.entwurf.getSelectableObject(selOb.cloneIds[i]) as SelectableObject).cloneIds.push(clone.id);
        }
        selOb.cloneIds.push(clone.id);
        selOb.cloningAngleRad[clone.id] = origCloningAngleRad;

        clone.translation = new Vector2(newTranslation.x, newTranslation.y);
        clone.rotation = newRotation;
        clone.relSize = newRelSize;
        clone.partName = newPart;
        clone.cloningAngleRad[id] = cloneCloningAngleRad;
        clone.cloneIds.push(selOb.id);

        this.sortCloneIntoArray(selOb.type, clone);

        this.model.entwurf.getZOrderTable().insertEntryIntoZIndex(clone.id, clone.floating, zIndex);
        EventVerteiler.broadcast("cloneCreatedEvent", {origId: id, clone: clone, zIndex: zIndex});
    }


    /**
     * Ein Klon soll gelöscht werden
     * @param {string}  origId      id des Originals
     * @param {string}  cloneId      id des Klons, der gelöscht werden soll
     */
    public deleteClone(origId, cloneId) {

        let index:number, arr:any[];

        const selOb = this.model.entwurf.getSelOb(origId);

        if (selOb === null) {
            globalsObject.errorHandler.handleError("internal", "deleteClone", "could not delete Clone, because I couldn't find the original " + origId);
            return;
        }

        switch (selOb.type) {
            case SelObTypes.TEXT:
                arr = this.model.entwurf.getTextObjects();
                break;
            case SelObTypes.LOGO:
            case SelObTypes.STOCKLOGO:
            case SelObTypes.CHROMOLOGO:
                arr = this.model.entwurf.getLogoObjects();
                break;
            default:
                globalsObject.errorHandler.handleError("internal", "deleteClone", "unknown selObType " + selOb.type);
                return;
        }

        index = arrayIndexByKeyValue(arr, "id", cloneId);
        if (index === -1) {
            globalsObject.errorHandler.handleError("internal", "deleteClone", "could not find clone " + cloneId);
        }
        arr.splice(index, 1);
        index = selOb.cloneIds.indexOf(cloneId);
        if (index === -1) {
            globalsObject.errorHandler.handleError("internal", "deleteClone", "could not delete " + cloneId + "from cloneIds Array");
            return;
        }
        selOb.cloneIds.splice(index, 1);
        delete selOb.cloningAngleRad[cloneId];
        this.model.entwurf.getZOrderTable().deleteEntry(cloneId);

        EventVerteiler.broadcast(SELOBEVENTNAMES.CLONE_DELETED, {origId: origId, cloneId: cloneId});
    }


    private sortCloneIntoArray(typeOfSelob: string, clone) {
        switch (typeOfSelob) {
            case SelObTypes.TEXT:
                this.model.entwurf.addTextObject(clone);
                break;
            case SelObTypes.LOGO:
            case SelObTypes.CHROMOLOGO:
            case SelObTypes.STOCKLOGO:
                this.model.entwurf.addLogo(clone);
                break;
            default:
                globalsObject.errorHandler.handleError("internal", "cloneObject", "unrecognized type: " + typeOfSelob);
        }
    }

    public relSizeCalculated(id:string, relSize:Vector2, sizeInCmToDisplay:Vector2) {
        const selOb = this.model.entwurf.getSelectableObject(id);
        if (selOb !== null) {
            selOb.relSize = new Vector2(relSize.x, relSize.y);
        } else {
            globalsObject.errorHandler.handleError("internal", "relSizeCalculated", "Vorsicht, konnte relSize von " + id + " nicht setzen!");
            return;
        }

        if (typeof (sizeInCmToDisplay) !== "undefined") {
            this.displaySizeHint(id, sizeInCmToDisplay);
        }
        EventVerteiler.broadcast("relSizeCalculatedEvent", selOb.id);
    }

    public duplicateSelOb(id, duplicateToPartname?) {

        function adjustTranslation(duplicate: SelectableObject, dimension: string): SelectableObject {
            const adjustment = duplicate.translation[dimension] > 0.5 ? -0.05 : 0.05;
            duplicate.translation[dimension] += adjustment;
            return duplicate;
        }

        const selOb = this.model.entwurf.getSelOb(id) as SelectableObject;
        this.model.entwurf.setChangedSinceSave(true);
        const duplicate = ["x", "y"].reduce((dupl, dim) => adjustTranslation(dupl, dim), selOb.clone());
        duplicate.isPinned = false;
        duplicate.hasTackleTwill = selOb.hasTackleTwill;
        duplicate.berechnungsFeature = selOb.berechnungsFeature;
        // Wir haben jetzt eine besonderheit, wenn wir es auf einen anderen Part legen sollen.
        if (duplicateToPartname && duplicateToPartname !== selOb.partName) {
            // Wenn ja so verschiebe auf Zwilling
            duplicate.partName = duplicateToPartname;
            duplicate.translation.x = 1 - selOb.translation.x;
            duplicate.translation.y = selOb.translation.y;
            duplicate.rotation = selOb.rotation * -1;
        }


        this.sortDuplicateIntoProperArray(duplicate);
        duplicate.zOrder = this.model.entwurf.getZOrderTable().insertEntry(duplicate.id, duplicate.cloneIds, duplicate.floating);

        EventVerteiler.broadcast(SELOBEVENTNAMES.INSERTED, {selOb: duplicate, zIndex: duplicate.zOrder});
        EventVerteiler.broadcast("selObInsertedByUserEvent", duplicate);
        EventVerteiler.broadcast(SELOBEVENTNAMES.SELECTED, duplicate);
        EventVerteiler.broadcast("getPreisEvent");
    }

    private sortDuplicateIntoProperArray(duplicate) {
        switch (duplicate.type) {
            case SelObTypes.LOGO:
            case SelObTypes.CHROMOLOGO:
            case SelObTypes.STOCKLOGO:
                this.model.entwurf.addLogo(duplicate);
                break;
            case SelObTypes.TEXT:
                this.model.entwurf.addTextObject(duplicate);
                break;
            default:
                globalsObject.errorHandler.handleError("severe", "duplicateSelOb", "unknown SelOb Type!");
        }
    }

    /**
     * Ist ein Counterpart angegeben, wird ein Dialog geöffnet, der fragt, ob auf diesen Part dupliziert werden soll, ansonsten wird einfach
     * direkt this.duplicateSelOb ausgeführt
     */
    public prepareSelObDuplication(id: string, counterpart: OwayoKonfiPart) {

        const selOb = this.model.entwurf.getSelOb(id);
        if (selOb === null) {
            return;
        }
        if (selOb.cloneIds.length === 0 && counterpart) {
            EventVerteiler.broadcast("displayDialogEvent", {
                dialog: "duplicateOnCounterpart", callbacks: [
                    () => this.duplicateSelOb(id, counterpart.name),
                    () => this.duplicateSelOb(id)
                ]
            });
            return;
        }
        this.duplicateSelOb(id);

    }

    public changeSelObScaleUniform = (id, operator) => EventVerteiler.broadcast("changedSelObScaleUniformEvent", {id: id,operator: operator});

    public createBareLogoObject(logoID, idForLogoObject:string|null = null) {
        if (idForLogoObject === null) {
            idForLogoObject = createGUID();
        }
        const logoPoolEntry = this.model.logoPool.getLogoById(logoID) as LogoPoolEntry;
        return new Logo({
            id: idForLogoObject as string,
            partName: "",
            filename: logoPoolEntry.filename,
            pathToLogo: logoPoolEntry.logoURL,
            logoId: logoPoolEntry.id,
            isAILogo: logoPoolEntry.isAILogo,
            hasTackleTwill: false,
            berechnungsFeature: null,
            zuLoeschenBeiProduktUndDesignWechsel:0
        });
    }



    public createTextObject(textValue, isFixed, part=null, position:{x, y}) {
        const newText = new TextObject({value: textValue, isFixed: isFixed, hasTackleTwill: false, berechnungsFeature: null});


        if (this.model.entwurf.getTextObjects().length >= 1) {
            const tempText = (this.model.entwurf.getTextObjects() as TextObject[])[this.model.entwurf.getTextObjects().length - 1];
            newText.adoptColorsAndFontFromOtherTextObject(tempText);
        }
        if(!FontHandler.isAvailableFont(newText.font)) {
            const availableFont = FontHandler.getFirstAvailableFont();
            if(availableFont !== null) {
                newText.font = availableFont;
            }
        }


        this.model.entwurf.addTextObject(newText);

        EventVerteiler.broadcast(SELOBEVENTNAMES.CREATED, newText);
        EventVerteiler.broadcast("displayHintEvent", "HINT_TEXTOBJECT_CREATED");
        EventVerteiler.broadcast("broadcastUsedColorsEvent");
        this.sendUsedFontsToViewComponents();
        if(part !== null) {
            this.insertSelObById(newText.id, part, position.x, position.y, "byUser");
        }
    }


    // //nötig, falls die Schriftarten erst ankommen, nachdem bereits Textobjekte eingefügt wurden...
    // public reInitializeInsertedtextObjects() {
    //     if (this.model.entwurf === null) {
    //         return;
    //     }
    //     this.model.entwurf.textObjects.forEach((t)=>EventVerteiler.broadcast("reloadSelObEvent", {selOb: t}));
    // }


    public insertSelObsAfterProductChange() {
        this.insertSelObInArrayIfVorbelegung(this.model.entwurf.getLogoObjects());
        this.insertSelObInArrayIfVorbelegung(this.model.entwurf.getTextObjects());
        const entwurfTransferStorage = this.entwurfTransferProvider.getEntwurfTransferStorage();
        this.insertSelObsInArrayAfterProductChange(entwurfTransferStorage.logoObjects);
        this.insertSelObsInArrayAfterProductChange(entwurfTransferStorage.textObjects);

        this.checkZOrderTable();
        EventVerteiler.broadcast("selobsChangedAfterProductChangeEvent")
        entwurfTransferStorage.isProcessed = true;
    }

    private insertSelObInArrayIfVorbelegung(array) {
        let i, lenI, selOb;
        for (i = 0, lenI = array.length; i < lenI; i++) {
            selOb = array[i];
            if (selOb.isVorbelegung) {
                this.insertSelObFromStorage(selOb, this.model.entwurf.getZOrderTable().getZIndex(selOb.id));
            }
        }
    }

    private insertSelObsInArrayAfterProductChange(array) {
        let i, lenI, selOb, oldDimensions;
        const entwurfTransferStorage = this.entwurfTransferProvider.getEntwurfTransferStorage();
        for (i = 0, lenI = array.length; i < lenI; i++) {
            selOb = array[i];
            oldDimensions = entwurfTransferStorage.partSizes[selOb.partName];
            EventVerteiler.broadcast("tryInsertingObjectAfterProductChangeEvent", {
                selOb: selOb,
                oldPartDimensionsCm: oldDimensions,
                oldFeatures: entwurfTransferStorage.features,
                zIndex: selOb.zOrder
            });
        }
    }

    public insertSelObFromStorage(selOb:SelectableObject, zIndex) {
        selOb.cloneIds = [];
        // selOb.clonedByPortalLineNumber = [];
        if (!this.selObAlreadyPresent(selOb)) {
            this.insertSelObIntoProperArray(selOb);
        }

        this.model.entwurf.getZOrderTable().insertEntryIntoZIndex(selOb.id, selOb.floating, zIndex, false);
        EventVerteiler.broadcast(SELOBEVENTNAMES.INSERTED, {
            selOb: selOb,
            zIndex: zIndex,
            maybeAppendedToEntry: false
        });
    }

    private selObAlreadyPresent(selOb:SelectableObject) {
        return (
            arrayIndexByKeyValue(this.model.entwurf.getLogoObjects(), "id", selOb.id) > -1 ||
            arrayIndexByKeyValue(this.model.entwurf.getTextObjects(), "id", selOb.id) > -1);
    }

    private insertSelObIntoProperArray(selOb:SelectableObject) {
        switch (selOb.type) {
            case SelObTypes.LOGO:
            case SelObTypes.CHROMOLOGO:
            case SelObTypes.STOCKLOGO:
                this.model.entwurf.addLogo(selOb as Logo|ChromoLogo|StockLogo);
                break;
            case SelObTypes.TEXT:
                this.model.entwurf.addTextObject(selOb as TextObject);
                break;
            default:
                globalsObject.errorHandler.handleError("internal", "insertSelObIntoProperArray", "unknown selOb Type " + selOb.type);
        }
    }

    private static pixelPerInch(imageDimensions : Vector2, sizeInCM : Vector2):Vector2 {
        return new Vector2(
            imageDimensions.x / sizeInCM.x * 2.54, //1 inch = 2.54 cm
            imageDimensions.y / sizeInCM.y * 2.54
        );
    }

    private getPPIFromLogoID(logoID: string, sizeInCmToDisplay: Vector2): Vector2 {
        const logoPoolEntry = this.model.logoPool.getLogoById(logoID);
        if (logoPoolEntry === null) {
            return new Vector2();
        }
        const imageDimensions = new Vector2(logoPoolEntry.breiteOriginal, logoPoolEntry.hoeheOriginal);
        return SelObController.pixelPerInch(imageDimensions, sizeInCmToDisplay);
    }

    private static isVectorGraphicExtension = (extension:string) => CONSTANTS.VECTOR_GRAPHICS_EXTENSIONS.indexOf(extension.toLowerCase()) > -1;

    public getPixelPerInch(id, sizeInCM): PPIInfo {
        const selOb = this.model.entwurf.getSelectableObject(id) as Logo;
        const output = new PPIInfo();

        if (selOb.type !== SelObTypes.LOGO || SelObController.isVectorGraphicExtension(this.getFileExtensionForLogoObject(selOb))) {
            return output;
        }

        output.ppi = this.getPPIFromLogoID(selOb.logoId, sizeInCM);
        return output;
    }

    private getFileExtensionForLogoObject(selOb: Logo) {
        const logoPoolEntry = this.model.logoPool.getLogoByFilename(selOb.filename) as LogoPoolEntry;
        if (logoPoolEntry === null ||logoPoolEntry.filenameOriginal === null) {
            return (selOb.filename.split(".").pop() as string).toLowerCase();
        }
        return (logoPoolEntry.filenameOriginal.split(".").pop() as string).toLowerCase();
    }

    public setContrastColorOnTextObjectIfNeededWithoutCallingChangeSelOb(selObId, colorCodeOfBackground) {
        const backgroundColorValue = (this.model.entwurf.getColorObjectByColorCode(colorCodeOfBackground) as Color).colorValue;
        const selObColorValue = (this.getSelOb(selObId) as TextObject).colorValue;
        const farbAbstand = ColorController.getDeltaEFromIntValues(backgroundColorValue, selObColorValue);
        if (farbAbstand < CONSTANTS.DELTA_E_THRESHHOLD) {
            (this.getSelOb(selObId) as TextObject).colorValue = ColorUtils.getContrastColorIntToIntValue(backgroundColorValue);
        }
    }

    public setContrastColorOnTextObjectIfNeeded(selObId, colorCodeOfBackground) {
        const backgroundColorValue = (this.model.entwurf.getColorObjectByColorCode(colorCodeOfBackground) as Color).colorValue;
        const selObColorValue = (this.getSelOb(selObId) as TextObject).colorValue;
        const farbAbstand = ColorController.getDeltaEFromIntValues(backgroundColorValue, selObColorValue);
        if (farbAbstand < CONSTANTS.DELTA_E_THRESHHOLD) {
            this.changeSelOb(selObId, {colorValue: ColorUtils.getContrastColorIntToIntValue(backgroundColorValue)});
        }
    }

    private withTextAndLogoArraysDo = (func:(a)=>void)=>[this.model.entwurf.getLogoObjects(), this.model.entwurf.getTextObjects()].map(func);
    public deleteVorbelegungsSelObs = ()=>this.withTextAndLogoArraysDo(a=>this.deleteVorbelegungsSelObInArray(a));
    public insertVorbelegungsSelObs = ()=>this.withTextAndLogoArraysDo(a=>this.insertVorbelegungsSelObsInArray(a));

    private insertVorbelegungsSelObsInArray(selObarray: SelectableObject[]) {
        let i;
        const lenI = selObarray.length;
        for (i = 0; i < lenI; i++) {
            if (selObarray[i].isVorbelegung) {
                EventVerteiler.broadcast(SELOBEVENTNAMES.INSERTED, {
                    selOb: selObarray[i],
                    zIndex: this.model.entwurf.getZOrderTable().getZIndex(selObarray[i].id)
                });
            }
        }
    }

    public deleteVorbelegungsSelObInArray(selObarray: SelectableObject[]) {
        let i;
        for (i = selObarray.length - 1; i > -1; i--) {
            if (selObarray[i].isVorbelegung) {
                this.deleteSelOb(selObarray[i].id);
            }
        }
    }

    private static insertChromoLogoColorInSelOb(colorsWithID: any, logo: any) {
        let i;
        const anzColors = colorsWithID.length;
        for (i = 0; i < anzColors; i++) {
            if (logo.chromoLogoId === colorsWithID[i].chromoLogoId) {
                logo.colors = colorsWithID[i].colors;
            }
        }
    }

    public insertChromoLogoColorsIntoVorbelegungsSelObs(logosFromDB) {
        const colorsWithID = this.entwurfTransferProvider.getEntwurfTransferStorage().vorbelegungsChromoLogoColors;
        const anzLogos = logosFromDB.length;
        let i;

        for (i = 0; i < anzLogos; i++) {
            const logo = logosFromDB[i];
            SelObController.insertChromoLogoColorInSelOb(colorsWithID, logo);
        }
        return logosFromDB;
    }

    public displaySizeHint(id, sizeInCM) {
        if(globalsObject.owayoJSViewer.selectedObject === null) {
            return;
        }
        const dpiInfo = this.getPixelPerInch(id, sizeInCM);
        const displayDPI = dpiInfo.getMinPPI() >= 0;

        EventVerteiler.broadcast(
            "displaySizeHintEvent",
            {
                x:sizeInCM.x.toFixed(1),
                y:sizeInCM.y.toFixed(1),
                dpiWarning : displayDPI && dpiInfo.isBelowThreshhold(),
                displayDPI : displayDPI,
                dpi        : dpiInfo.getMinPPI()
            }
        );
    }

    deleteAllSelObs() {
        this.deleteSelObsInArray(this.model.entwurf.getLogoObjects());
        this.deleteSelObsInArray((this.model.entwurf.getTextObjects() as TextObject[]));
    }

    private deleteSelObsInArray(array: SelectableObject[]) {
        let element:SelectableObject;

        element = array[0];
        while (element) {
            this.deleteSelOb(element.id, true, "loggedout");
            element = array[0];
        }
    }

    private sendUsedFontsToViewComponents() {
        const output:string[] = [];
        const textObjects = this.model.entwurf.getTextObjects() as TextObject[];

        for(let i=0, lenI = textObjects.length; i<lenI; i++) {
            const value = textObjects[i].font;
            if(output.indexOf(value) === -1) {
                output.push(value);
            }
        }
        EventVerteiler.broadcast("updateUsedFontsEvent", {usedFonts : output});
    }

    public selObPartnameWasChangedDueToCapitalizationErrors(selOb:SelectableObject) {
        const selObOnControllerside = this.model.entwurf.getSelectableObject(selOb.id) as SelectableObject;
        selObOnControllerside.partName = selOb.partName;
        EventVerteiler.broadcast("selObPartnameWasChangedDueToCapitalizationErrorsEvent", {selObId:selOb.id, newName:selOb.partName});
    }

    public selObInitialisationRequested() {
        EventVerteiler.broadcast("initSelObsEvent",
            {
                objects     : (this.model.entwurf.getLogoObjects() as Array<any>).concat(this.model.entwurf.getTextObjects()),
                zOrderJSON  : JSON.stringify(this.model.entwurf.getZOrderTable().values)
            });
    }

    public makeAllLogosAndTextsUntouchable = ()=>this.withTextAndLogoArraysDo(a=>a.map(selOb=>selOb.makeUntouchable()));
    private checkZOrderTable() {
        if (this.model.entwurf.getZOrderTable().hasHoles()) {
            this.model.entwurf.getZOrderTable().patchHoles();
            EventVerteiler.broadcast("patchZOrderHolesEvent");
        }
    }

    private loadKK_VorbelegungsSelObs(callback?:()=>void) {
        const $this = this;
        axios.get("/konfigurator_php/actionmodul/getKK_VorbelegungsSelObs.php?model="+this.model.entwurf.getModel()+"&design="+this.model.entwurf.getDesign())
            .then((response)=>{
                const entwurfTransferStorage = $this.entwurfTransferProvider.getEntwurfTransferStorage();
                if (entwurfTransferStorage.vorbelegungsChromoLogoColors.length > 0) {
                    response.data.logos = $this.insertChromoLogoColorsIntoVorbelegungsSelObs(response.data.logos);
                    entwurfTransferStorage.vorbelegungsChromoLogoColors = [];
                }
                $this.model.entwurf.loadTextObjects(response.data.textObjects);
                $this.model.entwurf.loadLogoObjects(response.data.logos);
                if (typeof (callback) !== "undefined") {
                    callback();
                }
            })
    }

    public getTextObjects(): TextObject[] {
        return this.model.entwurf.getTextObjects() as TextObject[];
    }

    public getLogoObjects(): Logo[] {
        return this.model.entwurf.getLogoObjects();
    }

    public createSelObChangeCallback =  ()=>(id, changedProperties)=>this.changeSelObAndTellHistoryController(id, changedProperties);

    public recalcOutlineIfNecessary(id) {
        const selOb = this.getSelOb(id) as TextObject;
        if(selOb === null || selOb.type !== SelObTypes.TEXT || isNaN(selOb.outlineWidthLevel)) {
            return;
        }
        const newOutline = Textoutline.getOutlineWidthInPixelsFromPartSelOb(selOb);

        if(! valuesAreEqualWithinPrecision(newOutline, selOb.outlineWidth)) {
            this.changeSelOb(id, {outlineWidth: newOutline});
        }
    }

    public outlinePixelSizeChanged(id, outlineWidth) {
        const selOb= this.getSelOb(id) as TextObject;
        selOb.outlineWidth = outlineWidth;
    }

    static guessOutlineLevelIfNecessary(selectableObject:TextObject) {
        if(selectableObject.outlineColorValue === "<none>" || !isNaN(selectableObject.outlineWidthLevel)) {
            return;
        }
        selectableObject.outlineWidthLevel = Textoutline.getNearestOutlineLevel(selectableObject);
    }

    public dpiToLow(selObID):boolean {
        const dpiInfo = this.getPixelPerInch(selObID, globalsObject.owayoJSViewer.getPartSelObSizeInCM(selObID)) as PPIInfo;
        return dpiInfo.getMinPPI() > 0 && dpiInfo.isBelowThreshhold();
    }

    public getChromoLogoColorsForTransferStorage() {
        const chromoLogos = this.getVorbelegungsChromoLogos();
        const anzChromoLogos = chromoLogos.length;
        const output = [] as {chromoLogoId, colors}[];
        let i;
        for (i = 0; i < anzChromoLogos; i++) {
            output.push({chromoLogoId: chromoLogos[i].chromoLogoId, colors: chromoLogos[i].colors});
        }
        return output;
    }

    private getVorbelegungsChromoLogos() {
        // eslint-disable-next-line prefer-const
        let chromoLogos = [] as ChromoLogo[], i:number;
        const logoObjects = this.model.entwurf.getLogoObjects();
        const anzLogos = logoObjects.length;

        for (i = 0; i < anzLogos; i++) {
            if ((logoObjects[i] as ChromoLogo).chromoLogoId !== null && logoObjects[i].isVorbelegung) {
                chromoLogos.push(logoObjects[i] as ChromoLogo);
            }
        }
        return chromoLogos;
    }

    private setTextObjectToDefaultFont(data:{ font:string, id:string }) {
        (this.model.entwurf.getTextObjects() as TextObject[]).find(t=>t.id === data.id).font = data.font;
    }
}
